import React from 'react';
import { Link } from 'react-router';
import type { LscVodCandidate } from '../types';
import { lscPath } from '../routing';
import {
  IGNORED_CANDIDATE_BACKFILL_LINK,
  IGNORED_CANDIDATE_BANNER_BODY,
  IGNORED_CANDIDATE_BANNER_FOOTER,
  IGNORED_CANDIDATE_BANNER_TITLE,
  IGNORED_CANDIDATE_IGNORED_AT_LABEL,
  IGNORED_CANDIDATE_IGNORED_BY_LABEL,
  IGNORED_CANDIDATE_IGNORED_BY_VALUE,
  IGNORED_CANDIDATE_NOTE_LABEL,
  IGNORED_CANDIDATE_REASON_LABEL,
} from '../commissionerReviewCopy';
import {
  formatIgnoredAtLabel,
  formatIgnoreReasonLabel,
  getCommissionerIgnoreMetadata,
} from '../archiveBackfillTruth';

export type IgnoredCandidateBannerProps = {
  candidate: LscVodCandidate;
  leagueId: string;
};

const IgnoredCandidateBanner: React.FC<IgnoredCandidateBannerProps> = ({
  candidate,
  leagueId,
}) => {
  const metadata = getCommissionerIgnoreMetadata(candidate);
  const reasonLabel = formatIgnoreReasonLabel(metadata?.reason);
  const ignoredAtLabel = formatIgnoredAtLabel(metadata?.ignored_at);

  return (
    <div
      className="rounded-lg border border-secondary-600 bg-secondary-900/50 p-4 text-sm space-y-3"
      data-testid="ignored-candidate-banner"
    >
      <div>
        <p className="font-medium text-white mb-1">{IGNORED_CANDIDATE_BANNER_TITLE}</p>
        <p className="text-secondary-300">{IGNORED_CANDIDATE_BANNER_BODY}</p>
      </div>
      <dl className="grid gap-2 sm:grid-cols-2">
        <div>
          <dt className="text-secondary-500">{IGNORED_CANDIDATE_REASON_LABEL}</dt>
          <dd className="text-secondary-100" data-testid="ignored-candidate-reason">
            {reasonLabel}
          </dd>
        </div>
        {metadata?.note?.trim() && (
          <div className="sm:col-span-2">
            <dt className="text-secondary-500">{IGNORED_CANDIDATE_NOTE_LABEL}</dt>
            <dd className="text-secondary-100 whitespace-pre-wrap" data-testid="ignored-candidate-note">
              {metadata.note.trim()}
            </dd>
          </div>
        )}
        {ignoredAtLabel && (
          <div>
            <dt className="text-secondary-500">{IGNORED_CANDIDATE_IGNORED_AT_LABEL}</dt>
            <dd className="text-secondary-100" data-testid="ignored-candidate-ignored-at">
              {ignoredAtLabel}
            </dd>
          </div>
        )}
        <div>
          <dt className="text-secondary-500">{IGNORED_CANDIDATE_IGNORED_BY_LABEL}</dt>
          <dd className="text-secondary-100" data-testid="ignored-candidate-ignored-by">
            {IGNORED_CANDIDATE_IGNORED_BY_VALUE}
          </dd>
        </div>
      </dl>
      <p className="text-secondary-400 text-xs">{IGNORED_CANDIDATE_BANNER_FOOTER}</p>
      <Link
        to={lscPath(`/leagues/${leagueId}/review?tab=backfill`)}
        className="inline-flex text-sm text-primary-300 hover:text-primary-200"
        data-testid="ignored-candidate-backfill-link"
      >
        ← {IGNORED_CANDIDATE_BACKFILL_LINK}
      </Link>
    </div>
  );
};

export default IgnoredCandidateBanner;
